import { Component, forwardRef, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { first } from 'rxjs/operators';
import { IBBModelForStepsHelper } from 'wizards/helpers/image-based-wizard-helpers/ibb-model-for-steps-helper';
import { IBBPlanFromStepsHelper } from 'wizards/helpers/image-based-wizard-helpers/ibb-plan-from-steps-helper';
import { PlanResponseModel } from 'wizards/models/base/plan-response-model';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { IBBFormModel } from '../../models/ibb/ibb-form-model';
import { WizardsServicesHubService } from '../../services/wizards-services-hub.service';
import { WizardBaseClass } from '../wizard-base.class';

type IBBWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'whatToBackUp'
  | 'advancedOptions'
  | 'compressionEncryption'
  | 'fullConsistencyCheck'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-image-based-wizard',
  templateUrl: './image-based-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => ImageBasedWizardComponent)
    }
  ]
})
export class ImageBasedWizardComponent extends WizardBaseClass {
  public restoreVerificationEnabled = true;
  public fromGettingStarted = false;
  public helpDocumentationLinks: Record<IBBWizardHelpDocumentationLinks, string>;
  @ViewChild(WizardComponent, { static: true }) wizard: WizardComponent;
  constructor(
    services: WizardsServicesHubService,
    public i18nPipe: I18NextPipe,
    stepService: WizardStepsService,
    helpService: HelpService
  ) {
    super(services, i18nPipe, stepService, helpService);
    this.fromGettingStarted = services.mainService.fromGettingStarted;

    this.FormModel = new IBBFormModel(this.isNBF, this.isUnix);
    if (!this.isRDMode || (this.isRDMode && this.isEdit)) {
      this.myPlan$
        .pipe(
          first((p) => !!p),
          untilDestroyed(this)
        )
        .subscribe((plan) => plan && this.updateStepData(plan));
      return;
    }
    this.updateStepData(new PlanResponseModel(), true);
  }

  updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) {
      // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
      if (!this.isEdit && this.isNBF && !this.isUnix) {
        this.FormModel.scheduleStep.ScheduleType = 'noschedule';
        this.FormModel.scheduleStep.fullBackupData.enabled = false;
      }

      return this.setDefaultValue();
    }

    const formData = IBBModelForStepsHelper.IBBModelFromPlan(plan, this.defaultSettings());

    if (this.isRDMode) formData.advancedOptionsFlags = this.FormModel.advancedOptionsFlags;

    this.FormModel = formData;

    // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
    const rightVersionForSchedule = this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 2) >= 80;
    if (!this.isEdit && this.isNBF && !this.isUnix && !rightVersionForSchedule) {
      this.FormModel.scheduleStep.ScheduleType = 'noschedule';
      this.FormModel.scheduleStep.fullBackupData.enabled = false;
    }
  }

  restoreVerificationEnabledHandler(event: boolean): void {
    this.restoreVerificationEnabled = event;
  }

  protected loadInfoHandlerFinally(event?: StepLoadInfo) {
    if (this.currentStep.title === 'Consistency Check and Restore Verification' && (!event || !event.loading) && event.isHyperV) {
      this.wizard.changeStep(1);
      const step = this.wizard.steps.find((s) => s.title === 'Schedule Options');

      if (step) {
        this.wizard.currentStep.valid = true;
        this.wizard.currentStep = step;
      }
    }
  }

  protected initHelpDocumentationLinks(): void {
    this.helpDocumentationLinks = {
      welcome: 'backup/ibb/nbf#plan',
      whereToBackUp: 'backup/ibb/nbf#where',
      whatToBackUp: 'backup/ibb/nbf#what',
      advancedOptions: 'backup/ibb/nbf#aoptions',
      compressionEncryption: 'backup/ibb/nbf#compression',
      fullConsistencyCheck: 'backup/ibb/nbf#fcc',
      schedule: 'backup/ibb/nbf#schedule',
      retention: 'backup/ibb/nbf#retention',
      prePost: 'backup/ibb/nbf#prepost',
      notification: 'backup/ibb/nbf#notifications'
    };

    super.initHelpDocumentationLinks();
  }

  handleSave(): void {
    const newPlan = IBBPlanFromStepsHelper.getUpdatedPlan(
      this.loadedPlan.plan,
      this.FormModel,
      this.defaultSettings(),
      this.isNBF,
      this.isUnix
    );
    this.savePlan(newPlan);
  }
}
